import React, { useState } from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { FormControl, FormLabel, SxProps } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import './CustomDatePicker.scss';

dayjs.extend(customParseFormat);

interface IProps {
  onChange: (value: string) => void;
  label?: string;
  value?: string | null;
  sx?: SxProps;
  disabled?: boolean;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  error?: boolean;
  helperText?: string;
}

const parseDateValue = (value: string): dayjs.Dayjs | null => {
  // Try ISO format first (YYYY-MM-DD), then German format (DD.MM.YYYY)
  const isoDate = dayjs(value, 'YYYY-MM-DD', true);
  if (isoDate.isValid()) return isoDate;
  const germanDate = dayjs(value, 'DD.MM.YYYY', true);
  if (germanDate.isValid()) return germanDate;
  // Fallback to dayjs default parsing
  const fallback = dayjs(value);
  return fallback.isValid() ? fallback : null;
};

export const CustomDatePicker: React.FC<IProps> = ({ sx, label = 'Datum', onChange, value, disabled, minDate, maxDate, error = false, helperText }) => {
  const [touched, setTouched] = useState(false);
  const showError = touched && error;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormControl fullWidth sx={sx} error={showError} className={disabled ? 'psf-form-field-custom-date-picker--disabled' : ''}>
        {label && <FormLabel error={showError}>{label}</FormLabel>}
        <DatePicker
          minDate={minDate}
          maxDate={maxDate}
          format="DD.MM.YYYY"
          className="psf-form__item psf-form__item-input"
          value={value ? parseDateValue(value) : null}
          disabled={disabled}
          onChange={(newValue) => {
            if (!newValue) return;
            onChange(newValue.format('YYYY-MM-DD'));
            setTouched(false);
          }}
          slotProps={{
            textField: {
              error: showError,
              helperText: showError && helperText,
              onBlur: () => setTouched(true),
            },
          }}
          slots={
            disabled
              ? {
                  openPickerIcon: () => <LockIcon sx={{ color: '#969696', fontSize: '18px', marginRight: '4px' }} />,
                }
              : undefined
          }
        />
      </FormControl>
    </LocalizationProvider>
  );
};
